<template>
  <div class="PlanArt">
    <div class="PlanArt__preview" aria-hidden="true">
      <div class="PlanArt__week">
        <div class="PlanArt__weekTop">
          <span class="PlanArt__weekTitle">Your week</span>
          <span class="PlanArt__weekRange">{{ rangeLabel }}</span>
        </div>
        <div class="PlanArt__weekGrid">
          <div class="PlanArt__hero">
            <div class="PlanArt__label">
              <span>{{ todayLabel }} · today</span>
              <em>✓ Done</em>
            </div>
            <div class="PlanArt__heroMain">
              <div class="PlanArt__art">
                <i style="height: 30%"></i>
                <i style="height: 60%"></i>
                <i style="height: 90%"></i>
                <i style="height: 60%"></i>
                <i style="height: 30%"></i>
              </div>
              <div>
                <div class="PlanArt__heroTitle">Over-Unders 3×12</div>
                <div class="PlanArt__heroMeta">1 h 20 · 92 TSS · indoor</div>
              </div>
            </div>
            <div class="PlanArt__chips">
              <span class="PlanArt__chip PlanArt__chip--done">Completed</span>
              <span class="PlanArt__chip">View ride</span>
            </div>
          </div>
          <div class="PlanArt__day">
            <div class="PlanArt__label">
              <span>{{ dayLabel(1) }}</span>
            </div>
            <span class="PlanArt__restMark"></span>
            <div class="PlanArt__rideName PlanArt__rideName--muted">Recovery</div>
            <div class="PlanArt__rideMeta">No ride</div>
          </div>
          <div class="PlanArt__day">
            <div class="PlanArt__label">
              <span>{{ dayLabel(2) }}</span>
            </div>
            <div class="PlanArt__effort"><i style="width: 85%; background: #e53e3e"></i></div>
            <div class="PlanArt__rideName">VO2max 5×4</div>
            <div class="PlanArt__rideMeta">1 h 10 · 88 TSS</div>
          </div>
          <div class="PlanArt__day">
            <div class="PlanArt__label">
              <span>{{ dayLabel(3) }}</span>
            </div>
            <div class="PlanArt__effort"><i style="width: 100%; background: #48bb78"></i></div>
            <div class="PlanArt__rideName">Long Aerobic Ride</div>
            <div class="PlanArt__rideMeta">3 h 30 · 190 TSS</div>
          </div>
        </div>
        <div class="PlanArt__weekFoot">
          <div class="PlanArt__load">
            <b>Weekly load</b>
            <div class="PlanArt__track"><i style="width: 18%"></i></div>
            <span>92 / 510 TSS</span>
          </div>
          <div class="PlanArt__metric">
            <b>1 / 5</b>
            <span>rides complete</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// A cropped, oversized "Your week" card with sample sessions on this week's
// real dates, fading out at the edge so it reads as an illustration rather
// than a screenshot. Shared by the planner's empty state and the help article
// that explains how plans are built, so the picture of the product is drawn
// once and can never fall out of date.
import dayjs from 'utils/dayjs'

export default {
  computed: {
    today() {
      return dayjs().startOf('day')
    },
    rangeLabel() {
      const end = this.today.add(6, 'day')
      return this.today.month() === end.month() ? `${this.today.format('MMM D')}–${end.format('D')}` : `${this.today.format('MMM D')} – ${end.format('MMM D')}`
    },
    todayLabel() {
      return this.today.format('dddd')
    }
  },
  methods: {
    dayLabel(offset) {
      return this.today.add(offset, 'day').format('ddd D')
    }
  }
}
</script>

<style lang="postcss" scoped>
/* A quiet wash behind the illustration: a breath of the brand orange at the
   top left, cooling to the app's grey. */
.PlanArt {
  @apply flex items-center justify-center p-8;
  background: linear-gradient(135deg, #ffe4dc 0%, #edf2f7 50%, #d6dee8 100%);
}
.PlanArt__preview {
  @apply relative w-full overflow-hidden;
  max-width: 560px;
  height: 300px;
  padding: 0;
}
/* The card fades to transparent (a mask, not a painted overlay), so the
   gradient behind it shows through the fade. */
.PlanArt__preview {
  -webkit-mask-image: linear-gradient(to right, #000 62%, transparent 97%), linear-gradient(to bottom, #000 82%, transparent 100%);
  -webkit-mask-composite: source-in;
  mask-image: linear-gradient(to right, #000 62%, transparent 97%), linear-gradient(to bottom, #000 82%, transparent 100%);
  mask-composite: intersect;
}
.PlanArt__week {
  @apply overflow-hidden bg-white border border-gray-300 shadow;
  width: 760px;
  border-radius: 12px 0 0 0;
}
.PlanArt__weekTop {
  @apply flex items-baseline gap-2 px-6 py-5 border-b border-gray-200;
}
.PlanArt__weekTitle {
  @apply text-lg font-medium;
}
.PlanArt__weekRange {
  @apply text-xs text-gray-500;
}
.PlanArt__weekGrid {
  @apply grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  min-height: 190px;
}
.PlanArt__hero,
.PlanArt__day {
  @apply flex flex-col px-4 py-4 border-l border-gray-200;
}
.PlanArt__hero {
  @apply px-6 py-6 border-l-0;
  background: #fafafa;
}
.PlanArt__label {
  @apply flex items-center justify-between text-sm font-medium text-gray-900;
}
.PlanArt__label em {
  @apply text-xs not-italic font-medium tracking-wide text-green-700 uppercase;
}
.PlanArt__heroMain {
  @apply grid items-center gap-4 mt-4;
  grid-template-columns: 72px 1fr;
}
.PlanArt__art {
  @apply rounded-xl flex items-center justify-center gap-1 p-3 bg-gray-200;
  width: 72px;
  height: 72px;
}
.PlanArt__art i {
  @apply block w-2 rounded;
  background: #ff5933;
}
.PlanArt__heroTitle {
  @apply text-xl font-bold leading-tight tracking-tight;
}
.PlanArt__heroMeta {
  @apply mt-1 text-sm text-gray-600;
}
.PlanArt__chips {
  @apply flex gap-2 pt-4 mt-auto;
}
.PlanArt__chip {
  @apply inline-flex items-center px-4 text-xs font-medium text-gray-800 bg-white border rounded-lg;
  height: 36px;
}
.PlanArt__chip--done {
  @apply text-green-800 bg-green-100 border-transparent;
}
.PlanArt__restMark {
  @apply block w-6 h-px mx-auto my-auto bg-gray-300;
}
.PlanArt__effort {
  @apply w-full mt-3 overflow-hidden bg-gray-200 rounded-full;
  height: 5px;
}
.PlanArt__effort i {
  @apply block h-full rounded-full;
  background: #ecc94b;
}
.PlanArt__rideName {
  @apply mt-auto text-xs font-medium text-gray-900 truncate;
}
.PlanArt__rideName--muted {
  @apply text-gray-500;
}
.PlanArt__rideMeta {
  @apply mt-1 text-xs text-gray-500;
}
.PlanArt__weekFoot {
  @apply grid border-t border-gray-200;
  grid-template-columns: 2fr 1.4fr 1.6fr;
}
.PlanArt__weekFoot > div {
  @apply px-6 py-4 border-l border-gray-200;
}
.PlanArt__weekFoot > div:first-child {
  @apply border-l-0;
}
.PlanArt__load {
  @apply flex items-center gap-3;
}
.PlanArt__load b {
  @apply text-xs font-medium;
  min-width: 77px;
}
.PlanArt__load span {
  @apply whitespace-nowrap text-xs text-gray-500;
}
.PlanArt__track {
  @apply flex-1 h-2 overflow-hidden bg-gray-200 rounded-full;
}
.PlanArt__track i {
  @apply block h-full rounded-full;
  background: #ff5933;
}
.PlanArt__metric {
  @apply flex items-baseline justify-between;
}
.PlanArt__metric b {
  @apply whitespace-nowrap text-2xl font-bold leading-none;
}
.PlanArt__metric span {
  @apply text-xs text-gray-500;
}
</style>
